import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, MapPin, BadgeCheck, Clock } from 'lucide-react';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import { getMyStaffProfile, getPermissionCatalogue } from '../../redux/staffSlice';

// The landing screen for a staff member.
//
// HONEST ABOUT WHAT EXISTS. Their sub-role can already grant pharmacy, lab and
// radiology permissions — those modules are the next phases and their screens do
// not exist yet. So this shows what the account IS allowed to do and marks what
// has not shipped, rather than linking to pages that would 404. It is also how
// an admin verifies a new account end to end without waiting for Phase 1.
//
// Permissions come from /user-roles/me on every load (hooks/usePermissions),
// never from the token — so a role narrowed a minute ago is already reflected.

// Groups whose screens are not built yet. Remove an entry as its module ships.
// 'pharmacy' came off this list when the counter, medicine master, rack map,
// stock alerts and bills shipped — leaving it would tell a pharmacist their own
// working screens are still coming.
const NOT_YET_BUILT = new Set(['billing']);

export default function StaffDashboard() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { permissions, roleName, ready } = usePermissions();
    const { myProfile, catalogue } = useSelector((s) => s.staff);

    useEffect(() => {
        dispatch(getMyStaffProfile());
        dispatch(getPermissionCatalogue());
    }, [dispatch]);

    // Only the groups this person actually holds something in — a receptionist
    // should not have to read past six empty pharmacy sections to find their own.
    const myGroups = useMemo(() => (catalogue || [])
        .map((g) => ({ ...g, held: g.permissions.filter((p) => permissions[p.id] === true) }))
        .filter((g) => g.held.length > 0), [catalogue, permissions]);

    const grantedTotal = Object.values(permissions || {}).filter(Boolean).length;

    if (!ready) {
        return <div className="flex justify-center py-20"><CommonLoader /></div>;
    }

    const fullName = `${myProfile?.name || ''} ${myProfile?.surname || ''}`.trim();

    return (
        <div className="w-full space-y-4" data-testid="staff-dashboard">
            <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {fullName
                        ? t('staffdash.greeting', { defaultValue: 'Welcome, {{name}}', name: fullName })
                        : t('staffdash.greeting_plain', 'Welcome')}
                </h1>

                <div className="flex flex-wrap gap-2 mt-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#4ABDBD]/10 text-[#2D8A8A] text-xs font-semibold">
                        <ShieldCheck className="w-4 h-4" />
                        {roleName || myProfile?.roleName || t('staffdash.no_role', 'No role assigned')}
                    </span>
                    {(myProfile?.departments || []).map((d) => (
                        <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold">
                            <MapPin className="w-4 h-4" />
                            {d}
                        </span>
                    ))}
                    {myProfile?.registrationNo && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold">
                            <BadgeCheck className="w-4 h-4" />
                            {myProfile.registrationNo}
                        </span>
                    )}
                </div>
            </div>

            <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-bold text-[#1a365d]">{t('staffdash.what_you_can_do', 'What you can do')}</h2>
                    <span className="text-xs text-slate-400">
                        {t('staffdash.granted', { defaultValue: '{{count}} permission', count: grantedTotal })}
                    </span>
                </div>

                {myGroups.length === 0 ? (
                    <div className="text-sm text-slate-500 py-6 text-center">
                        {t('staffdash.nothing_yet', 'Your role does not grant anything yet. Ask your administrator to add the permissions you need.')}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {myGroups.map((g) => (
                            <div key={g.id} data-testid={`staff-group-${g.id}`} className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-gray-50 to-blue-50/40 border-b border-gray-200">
                                    <span className="text-sm font-semibold text-[#1a365d]">{g.label}</span>
                                    {NOT_YET_BUILT.has(g.id) && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                            <Clock className="w-3 h-3" />
                                            {t('staffdash.coming_soon', 'Coming soon')}
                                        </span>
                                    )}
                                </div>
                                <ul className="divide-y divide-gray-100">
                                    {g.held.map((p) => (
                                        <li key={p.id} className="px-4 py-2 text-xs text-gray-600">{p.label}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}

                {myGroups.some((g) => NOT_YET_BUILT.has(g.id)) && (
                    <p className="text-[11px] text-slate-400 mt-4">
                        {t('staffdash.coming_soon_note', 'Sections marked “Coming soon” are permissions your role already holds — their screens arrive with the pharmacy, lab and scan modules.')}
                    </p>
                )}
            </div>
        </div>
    );
}
